import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const TestimonialSection = ({ testimonials, activeCategory, categories }) => {
  const currentCategory = categories?.find(cat => cat?.id === activeCategory);
  const filteredTestimonials = testimonials?.filter(testimonial => testimonial?.category === activeCategory);

  const getSectionStyle = () => {
    switch (activeCategory) {
      case 'events':
        return 'bg-gradient-to-br from-rose-50 to-pink-50';
      case 'realestate':
        return 'bg-gradient-to-br from-blue-50 to-slate-50';
      case 'marketing':
        return 'bg-gradient-to-br from-orange-50 to-red-50';
      case 'design':
        return 'bg-gradient-to-br from-gray-900 to-black';
      default:
        return 'bg-gray-50';
    }
  };

  const getCardStyle = () => {
    switch (activeCategory) {
      case 'events':
        return 'bg-white/80 backdrop-blur-sm border border-rose-100 shadow-lg';
      case 'realestate':
        return 'bg-white/90 backdrop-blur-sm border border-blue-100 shadow-md';
      case 'marketing':
        return 'bg-white/85 backdrop-blur-sm border border-orange-100 shadow-xl';
      case 'design':
        return 'bg-gray-800/50 backdrop-blur-sm border border-gray-700 shadow-2xl';
      default:
        return 'bg-white shadow-lg';
    }
  };

  if (filteredTestimonials?.length === 0) return null;

  return (
    <section className={`py-20 ${getSectionStyle()} transition-all duration-1000`}>
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className={`text-4xl font-bold mb-4 ${
            activeCategory === 'design' ? 'text-white' : 'text-gray-900'
          }`}>
            What Our {currentCategory?.name} Clients Say
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${
            activeCategory === 'design' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Real stories from real clients who trusted Skycrest Studios with their vision
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTestimonials?.map((testimonial, index) => (
            <div
              key={testimonial?.id}
              className={`${getCardStyle()} rounded-2xl p-8 transition-all duration-500 hover:scale-105`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="flex items-center mb-6">
                <div className="relative">
                  <Image
                    src={testimonial?.avatar}
                    alt={testimonial?.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  {testimonial?.isVideoTestimonial && (
                    <div className="absolute -bottom-1 -right-1 p-1 rounded-full bg-white shadow-md">
                      <Icon 
                        name="Play" 
                        size={12} 
                        color={currentCategory?.accentColor || '#3B82F6'} 
                      />
                    </div>
                  )}
                </div>
                <div className="ml-4">
                  <h4 className={`font-semibold ${
                    activeCategory === 'design' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {testimonial?.name}
                  </h4>
                  <p className={`text-sm ${
                    activeCategory === 'design' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {testimonial?.role}
                  </p>
                  <p className={`text-xs ${
                    activeCategory === 'design' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {testimonial?.company}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center mb-3">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon
                      key={i}
                      name="Star"
                      size={16}
                      color={i < testimonial?.rating ? (currentCategory?.accentColor || '#F59E0B') : '#E5E7EB'}
                      className="fill-current"
                    />
                  ))}
                </div>
                <blockquote className={`text-base leading-relaxed ${
                  activeCategory === 'design' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  "{testimonial?.content}"
                </blockquote>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name="Calendar" 
                    size={14} 
                    color={activeCategory === 'design' ? '#9CA3AF' : '#6B7280'} 
                  />
                  <span className={`text-sm ${
                    activeCategory === 'design' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {testimonial?.date}
                  </span>
                </div>
                
                {testimonial?.projectType && (
                  <span
                    className={`px-3 py-1 text-xs rounded-full ${
                      activeCategory === 'design' ?'bg-gray-700 text-gray-300' :'bg-gray-100 text-gray-700'
                    }`}
                    style={{
                      backgroundColor: activeCategory !== 'design' ? `${currentCategory?.accentColor}20` : undefined,
                      color: activeCategory !== 'design' ? currentCategory?.accentColor : undefined
                    }}
                  >
                    {testimonial?.projectType}
                  </span>
                )}
              </div>

              {testimonial?.isVideoTestimonial && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button className={`flex items-center space-x-2 text-sm font-medium transition-colors ${
                    activeCategory === 'design' ?'text-[#00D4AA] hover:text-[#00D4AA]/80' :'hover:opacity-80'
                  }`}
                  style={{
                    color: activeCategory !== 'design' ? currentCategory?.accentColor : undefined
                  }}>
                    <Icon name="Play" size={16} />
                    <span>Watch Video Testimonial</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;